import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PlatformLayout } from "../layouts/PlatformLayout";
import { useIdentity } from "../lib/identity";
import { fetchUserOverview, fetchUserCertificates, fetchUserBadges } from "../lib/sentinel-api";
import { getProgramById, levelColor, statusColor, type CertificationLevel, type ProgramStatus, type CertificationStatusRecord } from "../lib/certifications";
import type { CertificateRecord, BadgeRecord } from "../lib/sentinel-api";
import { fetchOrgInfo } from "../lib/portal-api";
import { Building2, Copy } from "lucide-react";

function ProgressBar({ value }: { value: number }) {
  return (
    <div style={{ width: "100%", height: "6px", background: "var(--neutral-200)", borderRadius: "var(--radius-full)", overflow: "hidden" }}>
      <div
        style={{
          width: `${value}%`,
          height: "100%",
          background: "linear-gradient(90deg, var(--accent-500), var(--accent-400))",
          borderRadius: "var(--radius-full)",
          transition: "width var(--transition)",
        }}
      />
    </div>
  );
}

function StatusBadge({ status }: { status: ProgramStatus }) {
  const labels: Record<ProgramStatus, string> = {
    not_started: "Not Started",
    in_progress: "In Progress",
    completed: "Completed",
    expired: "Expired",
  };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.375rem",
        padding: "0.25rem 0.625rem",
        borderRadius: "var(--radius-full)",
        fontSize: "0.6875rem",
        fontWeight: 600,
        background: `${statusColor(status)}15`,
        color: statusColor(status),
      }}
    >
      <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: statusColor(status) }} />
      {labels[status]}
    </span>
  );
}

function LevelPill({ level }: { level: CertificationLevel }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0.125rem 0.5rem",
        borderRadius: "var(--radius-sm)",
        fontSize: "0.6875rem",
        fontWeight: 700,
        background: `${levelColor(level)}15`,
        color: levelColor(level),
      }}
    >
      {level}
    </span>
  );
}

export default function DashboardPage() {
  const { identity, hasAnyRole } = useIdentity();
  const [certStatuses, setCertStatuses] = useState<CertificationStatusRecord[]>([]);
  const [certificates, setCertificates] = useState<CertificateRecord[]>([]);
  const [badges, setBadges] = useState<BadgeRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [portalSlug, setPortalSlug] = useState<string | null>(null);

  useEffect(() => {
    if (!identity.userId) {
      setLoading(false);
      return;
    }
    Promise.all([
      fetchUserOverview(identity.userId),
      fetchUserCertificates(identity.userId),
      fetchUserBadges(identity.userId),
    ]).then(([overview, certs, bdgs]) => {
      if (overview?.certifications?.length) setCertStatuses(overview.certifications);
      if (certs?.length) setCertificates(certs);
      if (bdgs?.length) setBadges(bdgs);
      setLoading(false);
    });
    if (identity.organizationId) {
      fetchOrgInfo(identity.organizationId).then((info: { portal_slug: string | null } | null) => {
        if (info?.portal_slug) setPortalSlug(info.portal_slug);
      });
    }
  }, [identity.userId, identity.organizationId]);

  if (loading) {
    return (
      <PlatformLayout>
        <div style={{ textAlign: "center", padding: "var(--space-8)" }}>
          <p className="text-muted">Loading your dashboard...</p>
        </div>
      </PlatformLayout>
    );
  }

  const isAdmin = hasAnyRole(["agency_admin", "enterprise_admin", "platform_admin"]);
  const activeCerts = certStatuses.filter((c) => c.status === "completed" || c.status === "in_progress");
  const upcomingRenewals = certStatuses.filter((c) => c.status === "expired" || (c.renewal_due_date && new Date(c.renewal_due_date) <= new Date("2027-01-01")));

  return (
    <PlatformLayout>
      {/* Hero */}
      <div
        style={{
          background: "linear-gradient(135deg, var(--primary-950), var(--primary-800))",
          borderRadius: "var(--radius-2xl)",
          padding: "var(--space-6) var(--space-5)",
          marginBottom: "var(--space-5)",
          color: "var(--text-on-dark)",
        }}
      >
        <h1 style={{ color: "var(--text-on-dark)", marginBottom: "0.5rem" }}>
          Welcome back, {identity.name ?? "Sentinel Operator"}
        </h1>
        <p className="lead lead-dark" style={{ fontSize: "1.0625rem", maxWidth: "600px" }}>
          Track your certifications, licenses, and training progress.
          {isAdmin && " Manage seats and licenses for your organization."}
        </p>
      </div>

      {/* Portal link card */}
      {portalSlug && (
        <div className="card" style={{ padding: "var(--space-3) var(--space-4)", marginBottom: "var(--space-4)", display: "flex", alignItems: "center", gap: "0.75rem", borderColor: "var(--accent-200)", background: "var(--accent-50)" }}>
          <Building2 size={20} style={{ color: "var(--accent-600)", flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--accent-700)" }}>Company Portal Link</div>
            <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>Share this link with employees to register:</div>
            <code style={{ fontSize: "0.75rem", color: "var(--accent-600)", wordBreak: "break-all" }}>{window.location.origin}/portal/{portalSlug}/register</code>
          </div>
          <button
            onClick={() => navigator.clipboard.writeText(`${window.location.origin}/portal/${portalSlug}/register`)}
            style={{ background: "none", border: "1px solid var(--accent-200)", borderRadius: "var(--radius-md)", padding: "0.375rem 0.625rem", cursor: "pointer", color: "var(--accent-600)", fontSize: "0.75rem", fontWeight: 600, display: "flex", alignItems: "center", gap: "0.25rem", flexShrink: 0 }}
          >
            <Copy size={14} /> Copy
          </button>
        </div>
      )}

      {/* Quick actions */}
      <div style={{ display: "flex", gap: "0.75rem", marginBottom: "var(--space-5)", flexWrap: "wrap" }}>
        <Link to="/certifications" className="btn btn-primary" style={{ fontSize: "0.875rem" }}>Browse Certifications</Link>
        <Link to="/certificates" className="btn btn-secondary" style={{ fontSize: "0.875rem" }}>View My Certificates & Badges</Link>
        <Link to="/capstones" className="btn btn-secondary" style={{ fontSize: "0.875rem" }}>Launch Capstone Simulations</Link>
        {isAdmin && <Link to="/seats" className="btn btn-secondary" style={{ fontSize: "0.875rem" }}>Manage Seats</Link>}
      </div>

      {/* Stats row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "0.75rem", marginBottom: "var(--space-5)" }}>
        <div className="card" style={{ padding: "var(--space-3) var(--space-4)" }}>
          <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--neutral-500)" }}>Active Certs</div>
          <div style={{ fontSize: "1.75rem", fontWeight: 800 }}>{activeCerts.length}</div>
        </div>
        <div className="card" style={{ padding: "var(--space-3) var(--space-4)" }}>
          <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--neutral-500)" }}>Certificates</div>
          <div style={{ fontSize: "1.75rem", fontWeight: 800 }}>{certificates.length}</div>
        </div>
        <div className="card" style={{ padding: "var(--space-3) var(--space-4)" }}>
          <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--neutral-500)" }}>Badges</div>
          <div style={{ fontSize: "1.75rem", fontWeight: 800 }}>{badges.length}</div>
        </div>
        <div className="card" style={{ padding: "var(--space-3) var(--space-4)" }}>
          <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--neutral-500)" }}>Licenses</div>
          <div style={{ fontSize: "1.75rem", fontWeight: 800 }}>{identity.activeLicenses.length}</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "var(--space-4)" }}>
        {/* Active Certifications */}
        <div className="card" style={{ padding: "var(--space-4)" }}>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Active Certifications</h2>
          {activeCerts.length === 0 ? (
            <div style={{ textAlign: "center", padding: "var(--space-4)" }}>
              <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "0.75rem" }}>No active certifications yet.</p>
              <Link to="/certifications" className="btn btn-primary" style={{ fontSize: "0.8125rem" }}>Browse Catalog</Link>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.875rem" }}>
              {activeCerts.map((cert) => {
                const program = getProgramById(cert.program_id);
                if (!program) return null;
                return (
                  <div key={cert.program_id} style={{ padding: "0.875rem", borderRadius: "var(--radius-lg)", border: "1px solid var(--border)", background: "var(--bg-subtle)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "0.875rem", marginBottom: "0.25rem" }}>{program.industry}</div>
                        <LevelPill level={program.level} />
                      </div>
                      <StatusBadge status={cert.status} />
                    </div>
                    <div style={{ margin: "0.75rem 0 0.5rem" }}>
                      <ProgressBar value={cert.status === "completed" ? 100 : 50} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{cert.status === "completed" ? "100" : "50"}% complete</span>
                      <Link to={`/programs/${cert.program_id}`} className="btn btn-primary" style={{ padding: "0.375rem 0.875rem", fontSize: "0.75rem" }}>
                        {cert.status === "completed" ? "Review" : "Continue"}
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Upcoming Renewals */}
        <div className="card" style={{ padding: "var(--space-4)" }}>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Upcoming Renewals</h2>
          {upcomingRenewals.length === 0 ? (
            <p className="text-muted" style={{ fontSize: "0.875rem" }}>No renewals due soon.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.875rem" }}>
              {upcomingRenewals.map((cert) => {
                const program = getProgramById(cert.program_id);
                if (!program) return null;
                const overdue = cert.status === "expired";
                return (
                  <div key={cert.program_id} style={{ padding: "0.875rem", borderRadius: "var(--radius-lg)", border: "1px solid var(--border)", background: overdue ? "var(--error-50)" : "var(--bg-subtle)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.375rem" }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "0.875rem" }}>{program.industry}</div>
                        <LevelPill level={program.level} />
                      </div>
                    </div>
                    <div style={{ fontSize: "0.75rem", color: overdue ? "var(--error-600)" : "var(--text-muted)", marginBottom: "0.5rem" }}>
                      {overdue ? "Overdue" : "Due"}: {cert.renewal_due_date}
                    </div>
                    <Link to="/renewals" className="btn btn-primary" style={{ padding: "0.375rem 0.875rem", fontSize: "0.75rem" }}>
                      Renew Now
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Admin: Assigned Seats */}
        {isAdmin && (
          <div className="card" style={{ padding: "var(--space-4)" }}>
            <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Organization Licenses</h2>
            {identity.activeLicenses.length === 0 ? (
              <div style={{ textAlign: "center", padding: "var(--space-3)" }}>
                <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "0.75rem" }}>No active licenses.</p>
                <Link to="/pricing" className="btn btn-primary" style={{ fontSize: "0.8125rem" }}>Purchase License</Link>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.875rem" }}>
                <div style={{ padding: "0.875rem", borderRadius: "var(--radius-lg)", border: "1px solid var(--border)", background: "var(--bg-subtle)" }}>
                  <div style={{ fontWeight: 600, fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                    {identity.activeLicenses.length} active license{identity.activeLicenses.length > 1 ? "s" : ""}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>
                    Manage seat assignments for your team
                  </div>
                  <Link to="/seats" className="btn btn-secondary" style={{ marginTop: "0.5rem", padding: "0.375rem 0.875rem", fontSize: "0.75rem" }}>
                    Manage Seats
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </PlatformLayout>
  );
}
